/**
 * /finance shell: server-gated (useFinanceAccess) and a small section nav. Anyone without finance access is sent
 * home; the finance RPCs and RLS refuse them regardless.
 */
import { Navigate, NavLink, Outlet } from "react-router-dom";
import { BanknotesIcon } from "@heroicons/react/24/outline";
import clsx from "clsx";
import LoadingSpinner from "../../components/common/LoadingSpinner";
import { useFinanceAccess } from "../../hooks/useFinanceAccess";

export default function FinanceLayout() {
  const { access, visible, loading } = useFinanceAccess(null);
  if (loading) {
    return (
      <div className="flex justify-center py-16">
        <LoadingSpinner size="lg" text="Loading finance…" />
      </div>
    );
  }
  if (!visible || !access) {
    return <Navigate to="/" replace />;
  }
  const links = [
    { to: "/finance/expenses", label: "Expenses" },
    ...(access.is_approver || access.is_finance_admin
      ? [{ to: "/finance/approvals", label: "My approvals" }]
      : []),
    ...(access.is_finance_admin
      ? [
          { to: "/finance/ap", label: "AP queue & exports" },
          { to: "/finance/settings", label: "Finance settings" },
        ]
      : []),
  ];
  return (
    <div className="space-y-6">
      <div>
        <h1 className="flex items-center gap-2 text-2xl font-bold text-gray-900">
          <BanknotesIcon className="h-7 w-7 text-gray-500" /> Finance
        </h1>
        <nav
          className="mt-4 flex gap-4 border-b border-gray-200"
          aria-label="Finance sections"
        >
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              className={({ isActive }) =>
                clsx(
                  "-mb-px border-b-2 px-1 pb-3 text-sm font-medium",
                  isActive
                    ? "border-primary-500 text-primary-600"
                    : "border-transparent text-gray-500 hover:text-gray-700",
                )
              }
            >
              {l.label}
            </NavLink>
          ))}
        </nav>
      </div>
      <Outlet context={access} />
    </div>
  );
}
